// The "device pass" api-auth-gateway hands out after a new device passed OTP + MPIN. Sending it with the sign-in
// request is what makes the gateway skip the OTP next time. It is a credential for THIS browser only.
//
// The gateway issues one pass per USER and device, so a browser that several people sign in from must keep one pass per
// user: a single shared slot would be overwritten by whoever verified last, and everyone else would be asked for an OTP
// again. The user is the number typed at sign-in; it is hashed into the storage name so it is not readable there.
//
// Kept in a cookie AND localStorage: the app calls localStorage.clear() on logout, and a trusted device must stay
// trusted across logouts. Not a secret from scripts on the page (an XSS could read it), same as the login token.
const BASE_KEY = "bp_device_secret";
const OWNER_KEY = "bp_signin_owner";
const SHAPE = /^[A-Za-z0-9_-]{32,64}$/;
const TWO_YEARS_SECONDS = 60 * 60 * 24 * 365 * 2;

// FNV-1a: only a short, cookie-safe name. A collision between two users would at worst cost one OTP.
function keyFor(owner) {
  const who = typeof owner === "string" ? owner.trim().toLowerCase() : "";
  if (!who) return BASE_KEY;
  let h = 0x811c9dc5;
  for (let i = 0; i < who.length; i += 1) {
    h ^= who.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${BASE_KEY}_${h.toString(16)}`;
}

function readCookie(key) {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

function readStored(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

let currentOwner = null;

/** Remembers who is signing in, so the pass the gateway returns after OTP + MPIN is filed under that user. */
export function rememberSigninOwner(owner) {
  currentOwner = typeof owner === "string" && owner.trim() ? owner.trim() : null;
  try {
    if (currentOwner) sessionStorage.setItem(OWNER_KEY, currentOwner);
    else sessionStorage.removeItem(OWNER_KEY);
  } catch { /* private mode: the in-memory value is enough unless the page is reloaded mid-verification */ }
}

export function signinOwner() {
  if (currentOwner) return currentOwner;
  try {
    return sessionStorage.getItem(OWNER_KEY) || null;
  } catch {
    return null;
  }
}

export function getDeviceSecret(owner) {
  const key = keyFor(owner);
  return [readCookie(key), readStored(key)].find((v) => typeof v === "string" && SHAPE.test(v)) || null;
}

export function saveDeviceSecret(secret, owner = signinOwner()) {
  if (typeof secret !== "string" || !SHAPE.test(secret)) return;
  const key = keyFor(owner);
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${key}=${encodeURIComponent(secret)}; max-age=${TWO_YEARS_SECONDS}; path=/; SameSite=Lax${secure}`;
  } catch { /* cookies blocked: localStorage below is the fallback */ }
  try { localStorage.setItem(key, secret); } catch { /* private mode */ }
}
